import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { tPortalLib } from '../../locales';
import { PortalTheme } from '../../theme';
import { AppAdaptedTooltip } from '../app-adapted-tooltip.component';
import { EyeClosedIcon } from '../icons/eye-closed-icon.component';
import { EyeOpenIcon } from '../icons/eye-open-icon.component';

export interface AccountBalanceProps {
  id: string;
  type: 'CREDIT' | 'PREPAID';
  balance: number | undefined;
  maxCreditLimit: number | undefined;
  localeKey: string;
  currencyFormatter: (value: number, hideZeroDecimals?: boolean) => string;
  showBalance: boolean;
  toggleBalance: () => void;
}

/**
 * This Component should only be used when hidePrice is not enabled.
 * It displays the balance of an account, allowing users to toggle visibility.
 */

export const AccountBalance = (props: AccountBalanceProps) => {
  const theme = useTheme<PortalTheme>();

  const formattedBalance = useMemo(() => {
    if (props.type === 'CREDIT') {
      // Credit account balances are negative but should be displayed as positive.
      return props.currencyFormatter(Math.abs(props.balance!));
    }

    return props.currencyFormatter(props.balance!);
  }, [props.localeKey, props.balance]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="start"
        onClick={(e) => {
          props.toggleBalance();
          e.stopPropagation();
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: theme.card.contrastText,
          }}
        >
          {props.showBalance
            ? formattedBalance
            : tPortalLib.accountCard.balanceHiddenText()}
        </Typography>

        <AppAdaptedTooltip title={tPortalLib.accountCard.toggleBalance()}>
          <IconButton
            size="small"
            sx={{
              color: theme.card.action,
            }}
            aria-description={tPortalLib.accountCard.toggleBalance()}
          >
            {props.showBalance ? (
              <EyeOpenIcon stroke={theme.card.action} />
            ) : (
              <EyeClosedIcon stroke={theme.card.action} />
            )}
          </IconButton>
        </AppAdaptedTooltip>
      </Stack>

      {props.type === 'CREDIT' && (
        <Typography
          variant="body2"
          sx={{
            color: theme.card.contrastText,
          }}
        >
          {tPortalLib.accountCard.spentCreditOf({
            creditLimit: props.showBalance
              ? props.currencyFormatter(props.maxCreditLimit!)
              : tPortalLib.accountCard.balanceHiddenText(),
          })}
        </Typography>
      )}
    </>
  );
};
